/* eslint-disable max-lines */
import { useApolloClient, useSuspenseQuery } from '@apollo/client'
import {
  type CreateViewInput,
  FetchSheetCustomModelsDocument,
  type MyUserFieldsFragment,
  type SharedUrl,
  SheetThreadCommentNotificationsDocument,
  type ViewFieldsFragment,
  type ViewForSiderFieldsFragment,
  type ViewGroupFieldsFragment,
  type ViewUpdateInputSingle,
} from '@salescore/client-api'
import { Posthog, POSTHOG_EVENTS, Tracker } from '@salescore/client-base'
import {
  checkSyncEnabled,
  checkVisualizeEnabled,
  HandleQuery,
  SuspenseWithLoading,
  useCanForSalescoreObject,
  useCanForView,
  useMe,
} from '@salescore/client-common'
import { type CachedView, selectCurrentCachedView, useAppSelector } from '@salescore/client-redux'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  compileViewConfig,
  CORE_CONSTANT,
  type CoreModel,
  type ViewConfigAdditionalConfig,
  viewConfigKpiParameterSchema,
  type ViewConfigKpiPreset,
  type ViewQueryList,
} from '@salescore/core'
import { isSharedLinkPresetName, makeSharedLinkPresetName } from '@salescore/core'
import { App } from 'antd'
import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { RecoilRoot, useRecoilState, useRecoilValue } from 'recoil'
import { z } from 'zod'

import {
  type AppliedSharedUrlView,
  applySharedUrlToSheetView,
  type KpiPivotSharedUrlKind,
} from '../domain/applySharedUrlToView'
import { useSharedUrl } from '../hooks/useSharedUrl'
import { useStopUnload } from '../hooks/useStopUnload'
import { initializeState, type InitializeStateProperties, useReInitializeState } from '../recoil/hooks/initializeState'
import { useWatch } from '../recoil/hooks/useWatch'
import { kpiUserAppearanceAtom, sheetPickedPresetNameAtom } from '../recoil/navigation/atoms'
import { additionalConfigAtom } from '../recoil/records/atoms.ts'
import { useRefetchMutation } from '../recoil/records/mutations/useRefetchMutation'
import { changesAtom } from '../recoil/recordsHistory'
import {
  buildConvertedSheetSheetCustomModels,
  kpiFormModifiedAtom,
  type SalescoreObjectAbility,
  sheetCustomModelsAtom,
  type ViewAbility,
  viewsContextAtom,
} from '../recoil/view/atoms'
import type { ViewFieldsFragmentWithMetric } from '../recoil/view/attachMetricToKpiConfig'
import { ModelSearcherProvider } from '../recoil/view/selectors/modelSearcherSelector'
import { configAtom } from '../recoil/viewConfig'
import { HydrateViewState } from './HydrateViewState'
import { DndContextWrapper } from './view_ui/DndContextWrapper'
import { ViewUIComponentC, ViewUIContext } from './view_ui/ViewUIComponent'
import { ViewDrillDownModal } from './ViewDrillDownModal'
import { ViewDataLoading } from './ViewLoading' // ViewComponent内でviewの作成・更新を行う場合、サイドバー側のviewsも更新を行う必要がある。
import { VmidContextProvider } from './VmidContext'

// ViewComponent内でviewの作成・更新を行う場合、サイドバー側のviewsも更新を行う必要がある。
// これを円滑に行うため、これらのロジックはViewComponentには持たせず、注入する形にする
export type ViewContextCreateViewArgument = CreateViewInput & {
  shouldOpenAsTab?: boolean
}

export interface ViewsContext {
  models: CoreModel[]
  views: ViewFieldsFragmentWithMetric[]
  viewGroups: ViewGroupFieldsFragment[]
  viewsForSider: ViewForSiderFieldsFragment[]
  prevView?: ViewFieldsFragment
  parentViewProtectingUnload?: boolean
  shouldInitializeWithNewRecord?: boolean // このフラグが指定されているとき、queryは実行せず、1つの空レコードのみでrecordsを初期化する。フォームUIでの新規作成で利用。
  createView: (argument: ViewContextCreateViewArgument) => Promise<ViewFieldsFragment | undefined>
  updateView: (argument: ViewUpdateInputSingle) => Promise<ViewFieldsFragment | undefined>
  deleteView: (argument: { id: string }) => Promise<void>
  pickView: (viewId: string) => void
  refetchViews: () => Promise<void>
  onSaveView: (
    views: ViewFieldsFragment[],
    option?: { forceFetch?: boolean; shouldOpenAsTab?: boolean },
  ) => void | Promise<void>
  onAddResource: (argument: { viewIds?: string[] }) => Promise<void>
  onChangesChange?: (hasChange: boolean) => void
  hasChanges?: boolean
  updateCachedView?: (view: CachedView) => void
  setIsSaveConfigButtonActive?: (isActive: boolean) => void
  isSaveConfigButtonActive?: boolean
  isSheetMoreActionsCopyVisible?: boolean
  isChangedDrillDownModal?: boolean
  setIsChangedDrillDownModal?: (isChanged: boolean) => void
  isDrillDown?: boolean
  kpiPivotPresetName?: string
  hasChartDisplaySettingChanges?: boolean
  setHasChartDisplaySettingChanges?: (hasChanges: boolean) => void
}

export const ViewComponent = ({
  additionalConfig,
  isEditMode,
  onFinish,
  parameter,
  view,
  viewsContext,
  parentAbility,
}: {
  additionalConfig?: ViewConfigAdditionalConfig
  isEditMode?: boolean
  onFinish?: (argument?: { id?: string }) => void
  parameter: Record<string, unknown>
  view: ViewFieldsFragment
  viewsContext: ViewsContext
  parentAbility: (ViewAbility & SalescoreObjectAbility) | undefined
}): ReactElement => {
  const client = useApolloClient()
  const me = useMe()
  const sheetThreadCommentNotificationsQuery = useSuspenseQuery(SheetThreadCommentNotificationsDocument, {
    variables: {
      organizationId: me.organization.id,
    },
  })
  const {
    data: { sheetCustomModels },
  } = useSuspenseQuery(FetchSheetCustomModelsDocument, {
    variables: {
      organizationId: me.organization.id,
      viewId: view.id,
    },
  })
  const sharedUrl = useSharedUrl()
  const sharedUrlApplied = applySharedUrlToView(view, sharedUrl)
  const currentCachedView = useAppSelector((state) => selectCurrentCachedView(state, { viewId: view.id }))

  useEffect(() => {
    switch (view.type) {
      case 'sheet': {
        Posthog.track(POSTHOG_EVENTS.view_sheet, {
          viewId: view.id,
          prevViewId: view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)
            ? view.id.slice(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX.length)
            : viewsContext.prevView?.id,
          prevViewType: view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)
            ? 'kpi'
            : viewsContext.prevView?.type,
        })
        Tracker.shared().track({ category: 'view', value: `sheet` })
        break
      }
      case 'kpi': {
        Posthog.track(POSTHOG_EVENTS.view_kpi, {
          viewId: view.id,
          prevViewId: viewsContext.prevView?.id,
          prevViewType: viewsContext.prevView?.type,
        })
        Tracker.shared().track({ category: 'view', value: `kpi` })
        break
      }
      case 'kpiPivot': {
        Posthog.track(POSTHOG_EVENTS.view_dashboard, {
          viewId: view.id,
          prevViewId: viewsContext.prevView?.id,
          prevViewType: viewsContext.prevView?.type,
        })
        Tracker.shared().track({ category: 'view', value: `kpiPivot` })
        break
      }
      default: {
        break
      }
    }
  }, [])

  const [hasChangeToKpiPivotParameter, setHasChangeToKpiPivotParameter] = useState(
    currentCachedView?.hasChangeToKpiPivotParameter ?? false,
  )
  const [hasChangeToViewSchema, setHasChangeToViewSchema] = useState(currentCachedView?.hasChangeToViewSchema ?? false)

  const mustacheParameter = {
    ...parameter,
    // eslint-disable-next-line sonarjs/todo-tag
    ...me.myUser.resourceUser, // TODO: ネストさせる？
  }
  const recoilProperties = {
    client,
    mustacheParameter,
    additionalConfig,
    viewsContext,
  }

  // additionalConfigが変わったときにリフレッシュするかどうか
  // 基本的にするべきだが、KPIのシートのときは列設定するたびにadditionalConfigの中身が変わるので、リフレッシュしない
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: この「リフレッシュすべきかどうか」の判定ロジックが現状整理できておらず、必要かどうかもわかっていない。。
  //       一旦以下対応はしているが、そもそも全体的に見直ししたい
  const shouldRefreshWhenAdditionalConfigChanged = !view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)

  const computedAbility = useViewAbility(view, me.myUser)
  const ability = parentAbility ?? computedAbility

  return (
    <HandleQuery loadingElement={<ViewDataLoading view={view} />}>
      <VmidContextProvider>
        <RecoilRoot
          initializeState={initializeState({
            ...recoilProperties,
            sharedUrlApplied,
            cachedView: currentCachedView,
            sheetCustomModels,
            sharedUrl,
            sheetThreadCommentNotifications: sheetThreadCommentNotificationsQuery.data.sheetThreadCommentNotifications,
          })}
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: ここのキャッシュ条件がかなり微妙。。nameはKPIのためだけに指定している
          key={[
            view.id,
            view.name,
            shouldRefreshWhenAdditionalConfigChanged ? JSON.stringify(additionalConfig) : ``,
            sharedUrl?.id,
          ].join(',')}
        >
          <ModelSearcherProvider>
            <HydrateViewState viewId={view.id} loadingElement={<ViewDataLoading view={view} />}>
              <App component={false}>
                <SuspenseWithLoading type="custom" loadingElement={<ViewDataLoading view={view} />}>
                  <Body
                    key={sharedUrlApplied.view.id}
                    recoilProps={recoilProperties}
                    isEditMode={isEditMode}
                    parentViewProtectingUnload={viewsContext.parentViewProtectingUnload}
                    onFinish={onFinish}
                    onChangesChange={viewsContext.onChangesChange}
                    setIsSaveConfigButtonActive={viewsContext.setIsSaveConfigButtonActive}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                    onSaveView={viewsContext.onSaveView}
                    sharedUrlAppliedView={sharedUrlApplied.view}
                    ability={ability}
                    hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
                    setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
                    hasChangeToViewSchema={hasChangeToViewSchema}
                    setHasChangeToViewSchema={setHasChangeToViewSchema}
                  />
                </SuspenseWithLoading>
              </App>
            </HydrateViewState>
          </ModelSearcherProvider>
        </RecoilRoot>
      </VmidContextProvider>
    </HandleQuery>
  )
}

// 本当は各所のロジックでuseCanを呼び出したかったが、この中身はasync selectorであり、
// mutationから呼び出そうとするとエラーになってしまうため、ここで前もって計算した値を props 経由で子に渡すことにした。
// 結局残念な実装になってしまった感がある
const useViewAbility = (view: ViewFieldsFragment, user: MyUserFieldsFragment): ViewAbility & SalescoreObjectAbility => {
  const canCreate = useCanForView('create', view)
  const canUpdate = useCanForView('update', view)
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)
  const canDelete = useCanForView('delete', view)
  const canSaveRecord = useCanForView('save-record', view)
  const canCreateNewRecord = useCanForView('create-new-record', view)
  const canDeleteRecord = useCanForView('delete-record', view)
  const canOpenRelationInputForm = useCanForView('open-relation-input-form', view)
  const canDownloadCsv = useCanForView('download-csv', view)
  // ついでにライセンスのチェックも行なってしまう
  const isVisualizeEnabled = checkVisualizeEnabled(user)
  const isSyncEnabled = checkSyncEnabled(user)
  // SALESCOREカスタムオブジェクト単体で許可されているモデルがView内に1でもある場合操作可能とする
  // セルの編集だけは、編集するセルのオブジェクトに個別権限があるかで判定する
  // 保存済みのビューの設定から求めるため、保存前に足したノードのカスタムオブジェクトは、ビューを保存するまで個別権限では編集できない。
  // 保存権限がなく個別権限だけを持つユーザーがノードを足して保存せずに編集する場合に限られ、誤ったレコードへの書き込みではなく止まる側に倒れ、
  // 保存すれば解消するため受け入れる (!7744)。未保存の設定は RecoilRoot の中にしかなく、ここからは読めない
  const {
    canCreateSalescoreObjectRecord,
    canDeleteSalescoreObjectRecord,
    canEditSalescoreObjectRecord,
    canSaveSalescoreObject,
    editableModelNames,
  } = useCanForSalescoreObject(view)

  return useMemo(
    () => ({
      canCreate,
      canUpdate,
      canDisplayConfigEdit,
      canDelete,
      canCreateNewRecord,
      canSaveRecord,
      canDeleteRecord,
      isVisualizeEnabled,
      isSyncEnabled,
      canOpenRelationInputForm,
      canDownloadCsv,
      canSaveSalescoreObject,
      canEditSalescoreObjectRecord,
      canDeleteSalescoreObjectRecord,
      canCreateSalescoreObjectRecord,
      canEditModelRecord: (modelName: string | undefined) =>
        canSaveRecord || (isNotNullish(modelName) && editableModelNames.includes(modelName)),
    }),
    [
      canCreate,
      canUpdate,
      canDisplayConfigEdit,
      canDelete,
      canCreateNewRecord,
      canSaveRecord,
      canDeleteRecord,
      isVisualizeEnabled,
      isSyncEnabled,
      canOpenRelationInputForm,
      canDownloadCsv,
      canSaveSalescoreObject,
      canEditSalescoreObjectRecord,
      canDeleteSalescoreObjectRecord,
      canCreateSalescoreObjectRecord,
      editableModelNames,
    ],
  )
}

/**
 * Recoil の atom から現在の config / view / models / additionalConfig 等を読み取り、
 * `compileViewConfig` で `query` と `ui` を生成して `useMemo` で保持する custom hook。
 *
 * ViewComponent のコアロジックで、**ViewComponent を呼び出すたびに、ブラウザ側で
 * コンパイルが実行される** ことを意味します (依存値が変化すれば再 memo)。selector では
 * なく hook なのは過去のリファクタで変わった経緯あり (旧名 `compiledConfigSelector`)。
 */
const useCompiledConfig = ({
  view,
}: {
  view: ViewFieldsFragment
}):
  CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig | undefined => {
  const config = useRecoilValue(configAtom)
  const { views, models } = useRecoilValue(viewsContextAtom)
  const sheetCustomModels = useRecoilValue(sheetCustomModelsAtom)
  const me = useMe()
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const kpiUserAppearance = useRecoilValue(kpiUserAppearanceAtom)

  return useMemo(
    () =>
      compileViewConfig(
        config,
        {
          view,
          models: [...models, ...buildConvertedSheetSheetCustomModels(sheetCustomModels)],
          views,
          organizationSetting: {
            ...me.organization.setting,
            dimensionGroups: me.organization.dimensionGroups,
          },
        },
        {
          additionalConfig: {
            ...additionalConfig,
            kpiParameterPerUser: {
              totalRowOrder: kpiUserAppearance.totalRowOrder,
              totalColumnOrder: kpiUserAppearance.totalColumnOrder,
              skipTotal: kpiUserAppearance.skipTotal,
            },
          },
        },
      ),
    [
      config,
      view,
      models,
      sheetCustomModels,
      views,
      me.organization.setting,
      me.organization.dimensionGroups,
      additionalConfig,
      kpiUserAppearance.totalRowOrder,
      kpiUserAppearance.totalColumnOrder,
      kpiUserAppearance.skipTotal,
    ],
  )
}

const Body = ({
  recoilProps,
  isEditMode,
  parentViewProtectingUnload,
  onFinish,
  onChangesChange,
  setIsSaveConfigButtonActive,
  onSaveView,
  sharedUrlAppliedView,
  ability,
  hasChangeToKpiPivotParameter,
  setHasChangeToKpiPivotParameter,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  recoilProps: InitializeStateProperties
  isEditMode: boolean | undefined
  parentViewProtectingUnload: boolean | undefined
  onFinish: ((argument?: { id?: string }) => void) | undefined
  onChangesChange: ((hasChange: boolean) => void) | undefined
  setIsSaveConfigButtonActive: ((isActive: boolean) => void) | undefined
  onSaveView: (views: ViewFieldsFragment[]) => void
  sharedUrlAppliedView: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToKpiPivotParameter: (hasChangeToKpiPivotParameter: boolean) => void
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const [view, setView] = useState(sharedUrlAppliedView)
  const compiledConfig = useCompiledConfig({ view })
  useReInitializeState(recoilProps)
  const changes = useRecoilValue(changesAtom)
  const kpiFormModified = useRecoilValue(kpiFormModifiedAtom)
  const [sheetPresetName] = useRecoilState(sheetPickedPresetNameAtom)
  useStopUnload({
    parentViewProtectingUnload: parentViewProtectingUnload ?? false,
    changes,
    kpiFormModified,
    onChangesChange,
    hasChangeToKpiPivotParameter,
    hasChangeToViewSchema,
  })

  useEffect(() => {
    if (setIsSaveConfigButtonActive !== undefined) {
      setIsSaveConfigButtonActive(!isSharedLinkPresetName(sheetPresetName) && hasChangeToViewSchema)
    }
  }, [hasChangeToViewSchema, sheetPresetName])

  // (formでしか発生しないが)compiledConfigがundefinedのときは何も表示しない
  return compiledConfig === undefined ? (
    <></>
  ) : (
    <BodyImpl
      view={view}
      setView={setView}
      onSaveView={onSaveView}
      isEditMode={isEditMode}
      onFinish={onFinish}
      compiledConfig={compiledConfig}
      ability={ability}
      hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
      setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
      hasChangeToViewSchema={hasChangeToViewSchema}
      setHasChangeToViewSchema={setHasChangeToViewSchema}
    />
  )
}

const BodyImpl = ({
  isEditMode,
  onFinish,
  onSaveView,
  compiledConfig,
  view,
  setView,
  ability,
  hasChangeToKpiPivotParameter,
  setHasChangeToKpiPivotParameter,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  isEditMode: boolean | undefined
  onFinish: ((argument?: { id?: string }) => void) | undefined
  onSaveView: (views: ViewFieldsFragment[]) => void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  setView: (f: (previous: ViewFieldsFragment) => ViewFieldsFragment) => void
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToKpiPivotParameter: (hasChangeToKpiPivotParameter: boolean) => void
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const query: ViewQueryList | undefined =
    compiledConfig.type === 'sheet' || compiledConfig.type === 'form' ? compiledConfig.queries[0] : undefined
  useWatch({
    onSaveView,
    compiledConfig,
    view,
    ability,
    query,
    hasChangeToKpiPivotParameter,
    hasChangeToViewSchema,
    setHasChangeToViewSchema,
  })
  const recordsMutation = useRefetchMutation(view, query)

  const viewUiContextValue = useMemo(
    () => ({
      isEditMode: isEditMode ?? false,
      onFinish,
    }),
    [isEditMode, onFinish],
  )

  return (
    <ViewUIContext.Provider value={viewUiContextValue}>
      <DndContextWrapper isEditMode={isEditMode ?? false}>
        <>
          <div className="h-full">
            <ViewUIComponentC
              compiledConfig={compiledConfig}
              view={view}
              setView={setView}
              ability={ability}
              hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
              setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
              hasChangeToViewSchema={hasChangeToViewSchema}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />
          </div>

          {/* 汎用modalはここにおくべきだろうか？ */}
          <ViewDrillDownModal
            onFinish={async () => {
              await recordsMutation.refetch({ kind: 'reset' })
            }}
            compiledConfig={compiledConfig}
            view={view}
          />
        </>
      </DndContextWrapper>
    </ViewUIContext.Provider>
  )
}

function applySharedUrlToView(view: ViewFieldsFragment, sharedUrl: SharedUrl | undefined): AppliedSharedUrlView {
  if (sharedUrl === undefined) {
    return { view, kind: undefined }
  }
  switch (view.config.type) {
    case 'sheet': {
      return applySharedUrlToSheetView(view, sharedUrl)
    }
    case 'kpiPivot': {
      if (view.id !== sharedUrl.viewId) {
        return { view, kind: undefined }
      }
      const viewConfigKpiParameter = extractConfigKpiParameter(sharedUrl)
      if (viewConfigKpiParameter === undefined) {
        return { view, kind: undefined }
      }
      const parameter = z
        .object({
          kpiPivotPresetName: z.string().optional(),
        })
        .safeParse(sharedUrl.parameter)
      const sharedLinkPreset: ViewConfigKpiPreset = {
        name: makeSharedLinkPresetName(parameter.data?.kpiPivotPresetName),
        parameter: {
          ...viewConfigKpiParameter.data,
          pivot: {
            rows: Array.dedupeWith(
              viewConfigKpiParameter.data.pivot.rows,
              (a, b) =>
                [a.key, a.dateSpan].filter(isNotNullish).join(',') ===
                [b.key, b.dateSpan].filter(isNotNullish).join(','),
            ), // XXX: 不要なはずだが、どこかのロジックでKPIが重複することがあったので一応追加
            columns: Array.dedupeWith(
              viewConfigKpiParameter.data.pivot.columns,
              (a, b) =>
                [a.key, a.dateSpan].filter(isNotNullish).join(',') ===
                [b.key, b.dateSpan].filter(isNotNullish).join(','),
            ),
          },
        },
      }
      const { presets } = view.config
      // Remove the preset with the same name as the new preset
      const presetsWithoutSharedLink = presets?.filter((preset) => !isSharedLinkPresetName(preset.name)) ?? []
      const newPresets = [sharedLinkPreset, ...presetsWithoutSharedLink]
      const newView = {
        ...view,
        config: {
          ...view.config,
          presets: newPresets,
        },
      }
      return {
        view: newView,
        kind: viewConfigKpiParameter.kind,
      }
    }
    default: {
      break
    }
  }
  return { view, kind: undefined }
}

function extractConfigKpiParameter(sharedUrl: SharedUrl):
  | {
      data: z.infer<typeof viewConfigKpiParameterSchema>
      kind: KpiPivotSharedUrlKind
    }
  | undefined {
  const parsedKpiPivotSharedUrl = viewConfigKpiParameterSchema.safeParse(sharedUrl.parameter)
  if (parsedKpiPivotSharedUrl.success) {
    return {
      data: parsedKpiPivotSharedUrl.data,
      kind: 'kpiPivotSharedUrl',
    }
  }
  const parsedSheetThreadCommentInKpiPivotDrillDown = z
    .object({
      kpiPivotParameter: viewConfigKpiParameterSchema,
    })
    .safeParse(sharedUrl.parameter)
  if (parsedSheetThreadCommentInKpiPivotDrillDown.success) {
    return {
      data: parsedSheetThreadCommentInKpiPivotDrillDown.data.kpiPivotParameter,
      kind: 'sheetThreadCommentInKpiPivotDrillDown',
    }
  }
  return undefined
}
